<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-7xl rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div class="flex items-center gap-4">
          <div class="p-2 bg-brand-600 rounded-xl shadow-lg shadow-brand-500/20">
            <ShieldCheck class="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 class="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Dictamen Maestro de Comité</h2>
            <p class="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">EXP: {{ loanData?.code || 'Cargando...' }} • Analista: {{ approvingUser }}</p>
          </div>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full text-slate-400 hover:text-red-500 transition-colors">
          <X class="w-6 h-6" />
        </button>
      </div>

      <div class="flex overflow-x bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['px-6 py-3 text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all border-b-2',
          activeTab === tab.id ? 'border-brand-600 text-brand-600 bg-white dark:bg-slate-900' : 'border-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300']">
          {{ tab.name }}
        </button>
      </div>

      <div v-if="isLoadingDossier" class="flex-1 flex flex-col items-center justify-center p-20 bg-white dark:bg-slate-900">
        <Loader2 class="w-10 h-10 animate-spin text-brand-600 mb-4" />
        <p class="text-xs font-bold text-slate-400 uppercase tracking-widest">Sincronizando Expediente Único...</p>
      </div>

      <div v-else-if="dossier" class="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50/30 dark:bg-slate-950/20">
        <form id="committeeForm" @submit.prevent="handleSubmit" class="space-y-6">

          <div v-show="activeTab === 'customer'" class="space-y-6 animate-fade-in">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div class="lg:col-span-3 space-y-4">
                <div :class="sectionCardClasses">
                  <label :class="labelClasses" class="text-center w-full mb-2">Foto de Perfil</label>
                  <div class="w-32 h-32 rounded-2xl bg-slate-200 dark:bg-slate-700 mx-auto overflow-hidden border-2 border-white dark:border-slate-800 shadow-sm">
                    <img v-if="dossier.customer.photo_url" :src="dossier.customer.photo_url" class="w-full h-full object-contain" />
                    <User v-else class="w-full h-full p-6 text-slate-400" />
                  </div>
                  <div class="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                    <label :class="labelClasses" class="text-center w-full mb-2">Firma del Titular</label>
                    <div class="bg-white rounded-xl p-2 border border-slate-200">
                      <img v-if="dossier.customer.signature_base64" :src="dossier.customer.signature_base64" class="max-h-16 mx-auto dark:invert-0" />
                    </div>
                  </div>
                </div>
                <div :class="sectionCardClasses" class="space-y-3">
                  <label :class="labelClasses">Documentos DUI/ID</label>
                  <div class="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border dark:border-slate-700">
                    <img v-if="dossier.customer.document_front_url" :src="dossier.customer.document_front_url" class="w-full h-full object-contain" />
                    <div v-else class="w-full h-full flex flex-col items-center justify-center text-[8px] text-slate-400 font-bold uppercase"><CreditCard class="w-4 h-4 mb-1" /> Frontal</div>
                  </div>
                  <div class="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border dark:border-slate-700">
                    <img v-if="dossier.customer.document_back_url" :src="dossier.customer.document_back_url" class="w-full h-full object-contain" />
                    <div v-else class="w-full h-full flex flex-col items-center justify-center text-[8px] text-slate-400 font-bold uppercase"><CreditCard class="w-4 h-4 mb-1" /> Reverso</div>
                  </div>
                </div>
              </div>

              <div class="lg:col-span-9 space-y-6">
                <div :class="sectionCardClasses">
                  <h3 :class="sectionTitleClasses">Datos Maestros del Cliente</h3>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div><label :class="labelClasses">Nombre</label><p :class="dataClasses">{{ dossier.customer.full_name }}</p></div>
                    <div><label :class="labelClasses">Identificación</label><p :class="dataClasses" class="font-mono">{{ dossier.customer.identity_document }}</p></div>
                    <div><label :class="labelClasses">Nacimiento</label><p :class="dataClasses">{{ dossier.customer.birth_date }} ({{ calculateAge(dossier.customer.birth_date) }} años)</p></div>
                    <div><label :class="labelClasses">Género / Civil</label><p :class="dataClasses">{{ dossier.customer.gender === 'M' ? 'Masc.' : 'Fem.' }} • {{ dossier.customer.marital_status }}</p></div>
                    <div><label :class="labelClasses">Profesión</label><p :class="dataClasses">{{ dossier.customer.profession }}</p></div>
                    <div><label :class="labelClasses">Cargas</label><p :class="dataClasses">{{ dossier.customer.dependents_count }} Depend. / {{ dossier.customer.children_count }} Hijos</p></div>
                  </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div :class="sectionCardClasses">
                    <h3 :class="sectionTitleClasses">Vivienda y Localización</h3>
                    <div class="space-y-4">
                      <div><label :class="labelClasses">Dirección</label><p :class="dataClasses" class="text-xs">{{ dossier.customer.exact_address || dossier.customer.community }}</p></div>
                      <div class="grid grid-cols-2 gap-2">
                        <div><label :class="labelClasses">Tipo Vivienda</label><p :class="dataClasses">{{ dossier.customer.housing_type }}</p></div>
                        <div><label :class="labelClasses">Estabilidad</label><p :class="dataClasses">{{ dossier.customer.years_living_there }} años</p></div>
                      </div>
                    </div>
                  </div>
                  <div :class="sectionCardClasses">
                    <h3 :class="sectionTitleClasses">Perfil Laboral</h3>
                    <div class="grid grid-cols-2 gap-4 text-slate-900 dark:text-slate-100">
                      <div><label :class="labelClasses">Empresa</label><p :class="dataClasses">{{ dossier.customer.company_name }}</p></div>
                      <div><label :class="labelClasses">Cargo</label><p :class="dataClasses">{{ dossier.customer.job_position }}</p></div>
                      <div><label :class="labelClasses">Jefe Directo</label><p :class="dataClasses">{{ dossier.customer.immediate_boss }}</p></div>
                      <div><label :class="labelClasses">Antigüedad</label><p :class="dataClasses">{{ dossier.customer.years_working }} años</p></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'credit'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses" class="border-t-4 border-brand-600">
              <h3 :class="sectionTitleClasses">Detalles del Préstamo Solicitado</h3>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div><label :class="labelClasses">Producto</label><p class="text-sm font-bold text-brand-600  dark:text-slate-100">{{ dossier.product.name }}</p></div>
                <div><label :class="labelClasses">Monto</label><p class="text-xl font-black font-mono dark:text-white">${{ formatNum(dossier.loan.amount) }}</p></div>
                <div><label :class="labelClasses">Tasa / Interés</label><p class="text-sm font-bold dark:text-white">{{ dossier.loan.rate }}% ({{ dossier.product.interest_type }})</p></div>
                <div><label :class="labelClasses">Plazo</label><p class="text-sm font-bold dark:text-white">{{ dossier.loan.term }} Meses</p></div>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="bg-slate-900 rounded-3xl p-8 text-white flex justify-between items-center shadow-xl">
                <div>
                  <p class="text-[10px] font-black uppercase text-slate-400">Cuota Mensual</p>
                  <p class="text-4xl font-black font-mono text-brand-400">${{ formatNum(dossier.loan.estimated_quota) }}</p>
                </div>
                <BadgeDollarSign class="w-12 h-12 opacity-20" />
              </div>
              <div :class="sectionCardClasses" class="flex items-center justify-between dark:bg-slate-800">
                <div>
                  <p class="text-[10px] font-black uppercase text-slate-400 dark:text-slate-500">Índice de Cobertura (DSCR)</p>
                  <p :class="['text-4xl font-black font-mono', capacityColor.text]">{{ calculateDSCR() }}x</p>
                </div>
                <Activity class="w-10 h-10 opacity-20 dark:text-white" />
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'payments'" class="animate-fade-in">
             <div :class="sectionCardClasses" class="p-0 overflow-hidden border-none shadow-xl bg-white dark:bg-slate-800">
                <table class="w-full text-sm text-left">
                  <thead class="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-black bg-slate-100 dark:bg-slate-950 border-b dark:border-slate-700">
                    <tr><th class="px-6 py-4">Nº</th><th class="px-6 py-4">Vencimiento</th><th class="px-6 py-4 text-right">Capital</th><th class="px-6 py-4 text-right">Interés</th><th class="px-6 py-4 text-right font-bold text-brand-600">Total</th></tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
                    <tr v-for="p in dossier.payment_plans" :key="p.installment_number" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:text-slate-200">
                      <td class="px-6 py-3 font-bold">{{ p.installment_number }}</td>
                      <td class="px-6 py-3">{{ p.due_date }}</td>
                      <td class="px-6 py-3 text-right font-mono">${{ formatNum(p.capital) }}</td>
                      <td class="px-6 py-3 text-right font-mono">${{ formatNum(p.interest) }}</td>
                      <td class="px-6 py-3 text-right font-mono font-black text-brand-600">${{ formatNum(p.total_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
             </div>
          </div>

          <div v-show="activeTab === 'balance'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">Evaluación de la Actividad Comercial</h3>
              <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div class="md:col-span-2"><label :class="labelClasses">Nombre del Negocio</label><p :class="dataClasses">{{ dossier.evaluation.business_name }}</p></div>
                <div><label :class="labelClasses">Estabilidad</label><p :class="dataClasses">{{ getGlobalText(dossier.evaluation.business_stability, 9) }}</p></div>
                <div><label :class="labelClasses">Tipo/Giro</label><p :class="dataClasses">{{ getGlobalText(dossier.evaluation.business_type, 8) }}</p></div>
              </div>
              <div class="p-4 bg-slate-100 dark:bg-slate-800 rounded-2xl border dark:border-slate-700 mb-6">
                <label :class="labelClasses">Descripción de la Actividad</label>
                <p class="text-xs font-medium dark:text-slate-300 leading-relaxed">{{ dossier.evaluation.business_description }}</p>
              </div>

              <!-- VISOR DE FOTOS DEL NEGOCIO -->
              <div class="mb-6 grid grid-cols-2 gap-4">
                <div v-if="dossier.evaluation.photo_url_1" class="aspect-video rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                   <img :src="dossier.evaluation.photo_url_1" class="w-full h-full object-contain hover:scale-105 transition-transform" />
                </div>
                <div v-if="dossier.evaluation.photo_url_2" class="aspect-video rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                   <img :src="dossier.evaluation.photo_url_2" class="w-full h-full object-contain hover:scale-105 transition-transform" />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="space-y-4">
                  <label :class="labelClasses">Ventas Auditadas (Semana Tipo)</label>
                  <div class="grid grid-cols-7 gap-1">
                    <div v-for="day in daysMap" :key="day.key" class="text-center p-2 bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-xl">
                      <p class="text-[8px] font-black text-slate-400 uppercase">{{ day.label }}</p>
                      <p class="text-[10px] font-mono font-bold dark:text-white">${{ formatNum(dossier.evaluation['inc_'+day.key]) }}</p>
                      <div class="mt-1 text-[7px] font-black uppercase p-0.5 rounded" :class="getDayStatusClass(day.labelFull, dossier.evaluation)">{{ getDayShortLabel(day.labelFull, dossier.evaluation) }}</div>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div class="p-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-800">
                      <label :class="labelClasses" class="text-emerald-700">Ingreso Semanal</label>
                      <p class="text-lg font-mono font-black text-emerald-600">${{ formatNum(dossier.evaluation.total_weekly_income) }}</p>
                    </div>
                    <div class="p-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-800">
                      <label :class="labelClasses" class="text-emerald-700">Ingreso Mensual</label>
                      <p class="text-lg font-mono font-black text-emerald-600">${{ formatNum(dossier.evaluation.total_monthly_income) }}</p>
                    </div>
                  </div>
                </div>
                <div class="space-y-4">
                  <label :class="labelClasses">Egresos y Gastos Operativos</label>
                  <div class="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                    <div v-for="(exp, i) in parseJSON(dossier.evaluation.business_expenses_json)" :key="i"
                      class="flex justify-between p-2.5 bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-xl">
                      <span class="text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase">{{ exp.type }}: {{ exp.description }}</span>
                      <span class="text-sm font-mono font-bold text-red-500">-${{ formatNum(exp.amount) }}</span>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-4 mt-2">
                    <div class="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-800">
                      <label :class="labelClasses" class="text-red-700">Costo de Venta</label>
                      <p class="text-sm font-mono font-black text-red-600">-${{ formatNum(dossier.evaluation.estimated_monthly_cost) }}</p>
                    </div>
                    <div class="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-800">
                      <label :class="labelClasses" class="text-red-700">Deuda Externa</label>
                      <p class="text-sm font-mono font-black text-red-600">-${{ formatNum(dossier.evaluation.monthly_debt_payment) }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border dark:border-slate-700">
                <label :class="labelClasses">Observaciones del Analista de Campo</label>
                <p class="text-xs font-medium dark:text-slate-400 italic">"{{ dossier.evaluation.observations || 'Sin observaciones.' }}"</p>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'collaterals'" class="animate-fade-in grid grid-cols-1 md:grid-cols-2 gap-6 text-slate-800 dark:text-slate-200">
            <div v-for="col in dossier.collaterals" :key="col.id" :class="sectionCardClasses" class="border-l-8 border-brand-600 shadow-lg dark:bg-slate-800 dark:border-slate-700">
               <div class="flex justify-between items-start mb-6">
                <div>
                  <span class="px-2 py-0.5 bg-brand-100 dark:bg-brand-900 text-brand-700 dark:text-brand-300 text-[9px] font-black uppercase rounded">{{ col.collateral_type }}</span>
                  <h4 class="text-sm font-black mt-2 uppercase tracking-tight">{{ col.pledge_type || col.mortgage_type || 'Garantía Registrada' }}</h4>
                  <p class="text-[9px] font-bold text-slate-400 mt-1 uppercase">Propietario: {{ col.owner_name || dossier.customer.full_name }}</p>
                </div>
                <div class="text-right">
                  <p class="text-[9px] font-bold text-slate-400 uppercase">Valor Avalúo</p>
                  <p class="text-xl font-mono font-black text-emerald-600">${{ formatNum(col.guarantee_amount) }}</p>
                  <div class="mt-2 text-[8px] font-black px-1.5 py-0.5 bg-brand-600 text-white rounded">COBERTURA: {{ col.coverage_percentage }}%</div>
                </div>
              </div>

              <!-- VISOR DE FOTOS DE GARANTÍA -->
              <div class="grid grid-cols-2 gap-2 mb-4">
                <div v-if="col.photo_url_1" class="h-20 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                   <img :src="col.photo_url_1" class="w-full h-full object-contain" />
                </div>
                <div v-if="col.photo_url_2" class="h-20 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                   <img :src="col.photo_url_2" class="w-full h-full object-contain" />
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4 text-[10px] mb-4">
                <div class="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <span class="text-slate-400 block uppercase font-bold">Registro</span>
                  <span class="font-bold">{{ col.registration_number || 'N/A' }} ({{ col.registration_date || 'Sin fecha' }})</span>
                </div>
                <div class="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <span class="text-slate-400 block uppercase font-bold">Estado Legal</span>
                  <span class="font-bold text-emerald-600">{{ col.status }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'resolution'" class="animate-fade-in max-w-2xl mx-auto py-6">
            <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-[2.5rem] shadow-2xl overflow-hidden">
              <div class="bg-slate-900 p-8 text-center">
                <ShieldCheck class="w-12 h-12 text-white mx-auto mb-4" />
                <h3 class="text-xl font-black text-white uppercase tracking-widest">Acta de Resolución de Comité</h3>
                <p class="text-[10px] text-white mt-2 uppercase tracking-[0.2em]">Aprobador: {{ approvingUser }}</p>
              </div>
              <div class="p-10 space-y-8">
                <div class="grid grid-cols-2 gap-6">
                  <label :class="['p-4 border-2 rounded-2xl cursor-pointer transition-all flex items-center justify-center gap-3 font-black text-xs border-emerald-500 bg-emerald-50 text-emerald-700']">
                    <input type="radio" v-model="form.status" value="APPROVED" class="sr-only">
                    <CheckCircle class="w-5 h-5" /> AUTORIZAR
                  </label>
                  <label :class="['p-4 border-2 rounded-2xl cursor-pointer transition-all flex items-center justify-center gap-3 font-black text-xs border-red-500 bg-red-50 text-red-700']">
                    <input type="radio" v-model="form.status" value="REJECTED" class="sr-only">
                    <XCircle class="w-5 h-5" /> RECHAZAR
                  </label>
                </div>
                <div v-if="form.status === 'REJECTED'" class="space-y-2 animate-fade-in">
                  <label :class="labelClasses">Causa Formal de Denegación</label>
                  <select v-model="form.rejection_reason" required :class="inputClasses" class="dark:bg-slate-950">
                    <option value="" disabled>Seleccione una causa técnica...</option>
                    <option value="Capacidad de pago insuficiente">Capacidad de pago insuficiente</option>
                    <option value="Mal historial crediticio">Buró de Crédito Negativo</option>
                    <option value="Garantía insuficiente">Respaldo Colateral insuficiente</option>
                    <option value="Información Falsa">Inconsistencia en declaración de datos</option>
                  </select>
                </div>
                <div v-if="form.status" class="space-y-2 animate-fade-in">
                  <label :class="labelClasses">Notas Técnicas y Fundamentos</label>
                  <textarea v-model="form.committee_notes" rows="4" required :class="inputClasses" class="dark:bg-slate-950" placeholder="Detalle los puntos clave discutidos..."></textarea>
                </div>
              </div>
            </div>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center z-20">
        <div class="flex items-center gap-3">
          <p class="text-[9px] text-slate-400 font-mono font-bold uppercase tracking-widest"></p>
        </div>
        <div class="flex gap-3">
          <button @click="$emit('close')" type="button" :class="btnSecondary">Cerrar</button>
          <button @click="handleRever" type="button" :class="btnSecondary">Revertir</button>
          <button v-if="activeTab !== 'resolution'" @click="activeTab = 'resolution'" type="button" :class="btnPrimary">Ir a Dictamen</button>
          <button v-else type="submit" form="committeeForm" :disabled="isSaving || !form.status" :class="[btnPrimary, form.status === 'REJECTED' ? 'bg-red-600 hover:bg-red-700' : '']">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <ShieldCheck v-else class="w-4 h-4" /> Finalizar Sesión
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue';
import {
  X, Loader2, ShieldCheck, CheckCircle, XCircle, User, Briefcase,
  PieChart, Calendar, Key, ArrowUpRight, ArrowDownRight, BadgeDollarSign, Activity, CreditCard, Home
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';
import { useInformativeSummary } from '@/composables/reports/useInformativeSummary';
import Swal from 'sweetalert2';
const { generateSummary } = useInformativeSummary();

const props = defineProps({ show: Boolean, loanId: String });
const emit = defineEmits(['close', 'resolved']);
const { showSuccess, showError } = useToast();

const isSaving = ref(false);
const isLoadingDossier = ref(false);
const dossier = ref(null);
const activeTab = ref('customer');
const globalCatalog = ref([]); // Almacena el catálogo de Globals

const approvingUser = computed(() => SecureStorage.getItem('llevo_user')?.full_name || 'ADMINISTRADOR');

const dataClasses = "text-sm font-bold text-slate-900 dark:text-white uppercase";
const inputClasses = "w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all dark:text-white";
const labelClasses = "text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase mb-1 block tracking-widest";
const sectionCardClasses = "bg-slate-50/50 dark:bg-slate-800/40 p-6 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-sm";
const sectionTitleClasses = "text-xs font-black text-slate-900 dark:text-white flex items-center gap-2 mb-6 uppercase tracking-tight";
const btnPrimary = "px-8 py-3 text-[10px] font-black text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 uppercase tracking-widest";
const btnSecondary = "px-6 py-3 text-[10px] font-black text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors uppercase tracking-widest";
const loanData = ref({});
const tabs = [
  { id: 'customer', name: 'Perfil Cliente' },
  { id: 'credit', name: 'Crédito' },
  { id: 'payments', name: 'Plan Pagos' },
  { id: 'balance', name: 'Análisis Flujo' },
  { id: 'collaterals', name: 'Garantías' },
  { id: 'resolution', name: 'Resolución' },
];

const daysMap = [
  { key: 'monday', label: 'Lun', labelFull: 'Lunes' }, { key: 'tuesday', label: 'Mar', labelFull: 'Martes' }, { key: 'wednesday', label: 'Mié', labelFull: 'Miércoles' },
  { key: 'thursday', label: 'Jue', labelFull: 'Jueves' }, { key: 'friday', label: 'Vie', labelFull: 'Viernes' }, { key: 'saturday', label: 'Sáb', labelFull: 'Sábado' },
  { key: 'sunday', label: 'Dom', labelFull: 'Domingo' }
];

const form = reactive({ status: '', rejection_reason: '', committee_notes: '' });

const formatNum = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const parseJSON = (str) => { try { return typeof str === 'string' ? JSON.parse(str) : (str || []); } catch { return []; } };

const calculateAge = (date) => {
  if(!date) return 'N/A';
  const birth = new Date(date);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age--;
  return age;
};

// TRADUCTOR DE GLOBALS
const getGlobalText = (value, type) => {
    const item = globalCatalog.value.find(g => Number(g.type) === type && String(g.value) === String(value));
    return item ? item.text : (value || 'N/D');
};

const getDayStatusClass = (day, evalData) => {
  const good = parseJSON(evalData.good_days_json);
  const regular = parseJSON(evalData.regular_days_json);
  if (good.includes(day)) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
  if (regular.includes(day)) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
};

const getDayShortLabel = (day, evalData) => {
  const good = parseJSON(evalData.good_days_json);
  const regular = parseJSON(evalData.regular_days_json);
  if (good.includes(day)) return 'Bueno';
  if (regular.includes(day)) return 'Regular';
  return 'Malo';
};

const calculateDSCR = () => {
  if (!dossier.value?.evaluation) return "0.00";
  const monthlyNet = Number(dossier.value.evaluation.final_daily_available || 0) * 30;
  const quota = Number(dossier.value.loan?.estimated_quota || 1);
  return (monthlyNet / quota).toFixed(2);
};

const capacityColor = computed(() => {
  const ratio = Number(calculateDSCR());
  if (ratio >= 1.5) return { text: 'text-emerald-500' };
  if (ratio >= 1.0) return { text: 'text-amber-500' };
  return { text: 'text-red-500' };
});

watch(() => props.show, async (val) => {
  if (val && props.loanId) {
    // 1. Reset de estado inicial
    activeTab.value = 'customer';
    form.status = '';
    form.rejection_reason = '';
    form.committee_notes = '';
    dossier.value = null;
    isLoadingDossier.value = true;

    try {
      const user = SecureStorage.getItem('llevo_user');

      // PASO A: Obtener datos base del préstamo y catálogos
      const [loanRes, globalsRes] = await Promise.all([
        apiClient.get(`/loans/${props.loanId}`),
        apiClient.get(`/globals`)
      ]);

      globalCatalog.value = globalsRes.data.data || globalsRes.data;

      // Asignamos a loanData.value (es un ref reactivo)
      const rawLoan = Array.isArray(loanRes.data) ? loanRes.data[0] : (loanRes.data.data || loanRes.data);
      loanData.value = rawLoan;

      // Validación de seguridad de IDs
      if (!loanData.value?.customer_id || !loanData.value?.product_id) {
        throw new Error("El préstamo no tiene vinculado un cliente o producto válido.");
      }

      // PASO B: Obtener el resto del expediente de forma secuencial para evitar undefined
      const [customerRes, evaluationRes, collateralsRes, planRes, productRes] = await Promise.all([
        apiClient.get(`/customers/${loanData.value.customer_id}`),
        apiClient.get(`/financial_evaluations?customer_id=${loanData.value.customer_id}`),
        apiClient.get(`/collaterals?loan_id=${props.loanId}&company_id=${user.company}`),
        apiClient.get(`/payment_plans?loan_id=${props.loanId}`).catch(() => ({ data: [] })),
        apiClient.get(`/credit_products/${loanData.value.product_id}`)
      ]);

      // Procesamiento de respuestas
      const custRaw = customerRes.data?.data || customerRes.data;
      const evalData = Array.isArray(evaluationRes.data) ? evaluationRes.data[0] : (evaluationRes.data?.data || evaluationRes.data);
      const collatData = collateralsRes.data?.data || collateralsRes.data || [];
      const planData = planRes.data?.data || planRes.data || [];
      const prodData = productRes.data?.data || productRes.data;

      // --- VALIDACIÓN DE REGLAS DE NEGOCIO (GARANTÍAS) ---
      const typeRequired = prodData.guaranty_type; // NONE, PRENDARIA, HIPOTECARIA, MIXTA
      const hasPrendaria = collatData.some(c => c.collateral_type === 'PRENDARIA');
      const hasHipotecaria = collatData.some(c => c.collateral_type === 'HIPOTECARIA');

      let integrityError = null;

      if (!evalData) {
        integrityError = "No se encontró evaluación financiera para este cliente.";
      } else if (typeRequired === 'PRENDARIA' && !hasPrendaria) {
        integrityError = "Expediente Inválido: Este producto exige una garantía PRENDARIA.";
      } else if (typeRequired === 'HIPOTECARIA' && !hasHipotecaria) {
        integrityError = "Expediente Inválido: Este producto exige una garantía HIPOTECARIA.";
      } else if (typeRequired === 'MIXTA' && collatData.length === 0) {
        integrityError = "Expediente Inválido: Este producto requiere respaldo colateral (MIXTA).";
      }

      if (integrityError) {
        showError(integrityError);
        emit('close');
        return;
      }

      // Construcción del Dossier Único
      dossier.value = {
        loan: {
          ...loanData.value,
          estimated_quota: Number(loanData.value.total_amount || 0) / Number(loanData.value.term || 1)
        },
        customer: {
          ...custRaw,
          full_name: `${custRaw.first_name || ''} ${custRaw.middle_name || ''} ${custRaw.last_name || ''} ${custRaw.second_last_name || ''}`.replace(/\s+/g, ' ').trim()
        },
        evaluation: evalData,
        collaterals: collatData,
        payment_plans: planData,
        theoretical_plans: planData,
        product: prodData
      };

    } catch (e) {
      console.error("Error crítico en Dossier:", e);
      showError('Error al recuperar el expediente completo.');
      emit('close');
    } finally {
      isLoadingDossier.value = false;
    }
  }
});

const handleRever = async () => {
  // 1. Validación de seguridad final antes de procesar

  form.status='PENDING'
  isSaving.value = true;
  try {
    const payload = {
      status: form.status,
      //rejection_reason: form.status === 'REJECTED' ? form.rejection_reason : null,
      committee_notes: form.committee_notes,
      approved_by: approvingUser.value
    };

    const result = await Swal.fire({
      title: '¿Confirmar Revertir a Solicitud?',
      text: "Esta acción revertirá el crédito a su estado inicial y no se puede deshacer.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#2563eb', // Color azul/brand
      cancelButtonColor: '#ef4444', // Color rojo
      confirmButtonText: 'Sí, revertir',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) {
      return; // Si le da a cancelar o cierra el modal, cortamos todo aquí
    }

    // Llamada al endpoint de actualización de préstamo para el dictamen
    await apiClient.put(`/loans/${props.loanId}`, payload);

    showSuccess('Veredicto de comité guardado exitosamente.');
    handlePrintSummary();
    emit('resolved');
    emit('close');
  } catch (e) {
    console.error(e);
    showError('Error al guardar el veredicto. Intente de nuevo.');
  } finally {
    isSaving.value = false;
  }
};

const handleSubmit = async () => {
  // 1. Validación de seguridad final antes de procesar
  if (form.status === 'APPROVED') {
    const productType = dossier.value.product.guaranty_type;
    const collaterals = dossier.value.collaterals;

    // Doble check por si se borraron garantías en otra sesión
    if (productType !== 'NONE' && collaterals.length === 0) {
      return showError('No se puede aprobar: El producto requiere garantías y el expediente no tiene ninguna registrada.');
    }
  }

  isSaving.value = true;
  try {
    const payload = {
      status: form.status,
      rejection_reason: form.status === 'REJECTED' ? form.rejection_reason : null,
      committee_notes: form.committee_notes,
      approved_by: approvingUser.value
    };

    // Llamada al endpoint de actualización de préstamo para el dictamen
    await apiClient.put(`/loans/${props.loanId}`, payload);

    showSuccess('Veredicto de comité guardado exitosamente.');
    handlePrintSummary();
    emit('resolved');
    emit('close');
  } catch (e) {
    console.error(e);
    showError('Error al guardar el veredicto. Intente de nuevo.');
  } finally {
    isSaving.value = false;
  }
};

const handlePrintSummary = () => {
  console.log(dossier.value)
  if(dossier.value){
    generateSummary(
      dossier.value,{
        showPerfil: true,
        showProducto: true,
        showResolucion: true,
        showEvaluacion: true,
        showGarantias: true,
        showPlanTeorico: true,
        showPlanActivo: false,
        showTransacciones: false,
        showAnexos: true,
      },
      globalCatalog.value,
      'EXPEDIENTE INTEGRAL'
    );
  }
};
</script>